import { openExternalUrl } from '../../lib/zoom';
import { useEffect, useMemo, useState } from 'react';
import { EngineError, getMyAgenda, type AgendaSession } from '../../api/engineDashboard';

interface Props {
  token: string;
  isAdmin: boolean;
  onOpenCase: (caseId: string) => void;
  onUnauthorized: () => void;
}

type GroupKey = 'Today' | 'Tomorrow' | 'This week' | 'Later';

/** Local-time day boundaries; week = next 7 days including today. */
export function groupAgenda(sessions: AgendaSession[], now = new Date()) {
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = startOfDay(now).getTime();
  const day = 86_400_000;
  const groups: Record<GroupKey, AgendaSession[]> = { Today: [], Tomorrow: [], 'This week': [], Later: [] };
  let todayCount = 0;
  let weekCount = 0;
  for (const s of sessions) {
    const t = startOfDay(new Date(s.scheduled_start)).getTime();
    const key: GroupKey = t <= today ? 'Today' : t === today + day ? 'Tomorrow' : t < today + 7 * day ? 'This week' : 'Later';
    groups[key].push(s);
    if (t <= today) todayCount++;
    if (t < today + 7 * day) weekCount++;
  }
  return { groups, todayCount, weekCount };
}

const formatMeetingId = (id: string) => {
  const digits = id.replace(/\D/g, '');
  return digits.length === 11
    ? `${digits.slice(0, 3)} ${digits.slice(3, 7)} ${digits.slice(7)}`
    : digits.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
};

const timeOf = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const dayOf = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

/** "What's on my calendar" — a collapsed one-line summary by default
    (the ticket: the agenda ate the page; mediators want the case LIST).
    Expanding shows chronological groups, each its own drop-down; the
    expanded/collapsed choice sticks per browser. */
const OPEN_KEY = 'mc.upcoming.open';

export function AgendaPanel({ token, isAdmin, onOpenCase, onUnauthorized }: Props) {
  const [sessions, setSessions] = useState<AgendaSession[] | null>(null);
  const [open, setOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(OPEN_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({ Today: true });
  const toggleOpen = () => {
    setOpen((prev) => {
      try {
        localStorage.setItem(OPEN_KEY, prev ? '0' : '1');
      } catch {
        /* private mode etc. — preference just won't stick */
      }
      return !prev;
    });
  };

  useEffect(() => {
    getMyAgenda(token)
      .then((res) => setSessions(res.sessions))
      .catch((err: unknown) => {
        if (err instanceof EngineError && err.status === 401) return onUnauthorized();
        setSessions([]);
      });
  }, [token, onUnauthorized]);

  const { groups, todayCount, weekCount } = useMemo(
    () => groupAgenda(sessions ?? []),
    [sessions],
  );

  return (
    <section className="card">
      <button
        type="button"
        onClick={toggleOpen}
        className={`flex w-full flex-wrap items-center gap-3 p-3 text-left hover:bg-surface-muted ${open ? 'border-b border-surface-border' : ''}`}
        title={open ? 'Collapse the upcoming sessions' : 'Show the upcoming sessions'}
      >
        <span className="text-gray-400">{open ? '▾' : '▸'}</span>
        <div className="section-label">Upcoming</div>
        {sessions === null ? (
          <span className="text-xs text-gray-400">Loading…</span>
        ) : (
          <div className="flex items-baseline gap-4">
            <span>
              <span className="text-lg font-semibold tabular-nums">{todayCount}</span>
              <span className="ml-1 text-xs font-medium text-gray-600">today</span>
            </span>
            <span>
              <span className="text-lg font-semibold tabular-nums">{weekCount}</span>
              <span className="ml-1 text-xs font-medium text-gray-600">this week</span>
            </span>
            <span>
              <span className="text-lg font-semibold tabular-nums">{(sessions ?? []).length - weekCount}</span>
              <span className="ml-1 text-xs font-medium text-gray-600">later</span>
            </span>
          </div>
        )}
      </button>

      {open && sessions !== null && sessions.length === 0 && (
        <p className="p-4 text-center text-sm text-gray-500">No upcoming sessions.</p>
      )}

      {open && (Object.entries(groups) as Array<[GroupKey, AgendaSession[]]>)
        .filter(([, list]) => list.length > 0)
        .map(([label, list]) => (
          <div key={label}>
            <button
              type="button"
              onClick={() => setOpenGroups((g) => ({ ...g, [label]: !g[label] }))}
              className="flex w-full items-center gap-2 bg-surface-muted px-3 py-1 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-700"
            >
              <span>{openGroups[label] ? '▾' : '▸'}</span>
              {label}
              <span className="font-normal normal-case tracking-normal text-gray-400">({list.length})</span>
            </button>
            {openGroups[label] && (
            <ul className="divide-y divide-surface-border">
              {list.map((s) => (
                <li key={s.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-surface-muted">
                  <div className="w-24 shrink-0 text-right">
                    <div className="text-sm font-semibold tabular-nums">{timeOf(s.scheduled_start)}</div>
                    <div className="text-[11px] text-gray-400">{dayOf(s.scheduled_start)}</div>
                  </div>
                  <button type="button" className="min-w-0 flex-1 text-left" onClick={() => onOpenCase(s.case_id)}>
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-brand-subtle px-1.5 py-0.5 text-[11px] font-medium text-brand">
                        {s.session_type === 'joint' ? 'Joint' : '1-on-1'}
                      </span>
                      <span className="truncate text-sm font-medium">
                        {s.case_title_hint ?? s.claim_type ?? 'Mediation session'}
                      </span>
                    </div>
                    <div className="mt-0.5 truncate text-xs text-gray-500">
                      {s.parties.map((p) => p.name).join(' · ')}
                      {isAdmin && s.mediator_name ? ` — ${s.mediator_name}` : ''}
                    </div>
                    {s.phone_join && (
                      <div className="mt-0.5 text-[11px] text-gray-500" title="Read this to a party who can't launch Zoom — they can dial in from any phone">
                        📞 {s.phone_join.number} · ID {formatMeetingId(s.phone_join.meeting_id)}
                        {s.phone_join.passcode ? ` · passcode ${s.phone_join.passcode}` : ''}
                      </div>
                    )}
                  </button>
                  {s.start_url ? (
                    <button
                      type="button"
                      onClick={async () => {
                        const result = await openExternalUrl(s.start_url!);
                        if (result === 'copied') {
                          window.alert('Zoom blocked opening the link directly. It was COPIED to your clipboard: paste it into any browser to start as host.');
                        } else if (result === 'failed') {
                          window.alert(`Could not open or copy the link. Start link: ${s.start_url}`);
                        }
                      }}
                      className="btn-primary shrink-0 !py-1 !text-xs"
                      title="Start the session as host — this is what activates the in-meeting copilot"
                    >
                      Start as host
                    </button>
                  ) : (
                    s.join_url && (
                      <button
                        type="button"
                        onClick={async () => {
                          const result = await openExternalUrl(s.join_url!);
                          if (result !== 'opened') window.alert('Link copied to clipboard: paste it into a browser to join.');
                        }}
                        className="btn-secondary shrink-0 !py-1 !text-xs"
                      >
                        Join
                      </button>
                    )
                  )}
                </li>
              ))}
            </ul>
            )}
          </div>
        ))}
    </section>
  );
}
